const Buffer = require('buffer').Buffer // Add this line if you're in a Node.js environment

export async function getCachedResponse(url) {
  const key = window.xCacheKey

  if (!key) {
    return
  }

  try {
    const result = await _getCachedResponse(key, url)
    return result
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    return undefined
  }
}

export async function cacheResponse(url, data, ttl = 1000) {
  const key = window.xCacheKey

  if (!key) {
    return
  }

  try {
    await _cacheResponse(key, url, data, ttl)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
  }
}

async function _getCachedResponse(key, url) {
  const cache = await _getEncryptedCache(key)

  if (!cache) {
    return
  }

  const cachedData = cache[url]

  if (!cachedData) {
    return
  }

  const { data, ttl = 1000, createdAt = Date.now() } = cachedData

  if (Date.now() - createdAt > ttl * 1000) {
    await _removeCachedUrl(key, url)
    return
  }

  return data
}

async function _cacheResponse(key, url, data, ttl = 1000) {
  const cache = await _getEncryptedCache(key)

  cache[url] = {
    data,
    ttl,
    createdAt: Date.now()
  }

  await _setEncryptedCache(key, cache)
}

async function _setEncryptedCache(key, data = {}) {
  const stringified = JSON.stringify(data)

  const encrypted = await _encryptData(key, stringified)

  localStorage.setItem('x-cache', encrypted)
}

async function _removeCachedUrl(key, url) {
  const cache = await _getEncryptedCache(key)

  if (cache) {
    delete cache[url]

    await _setEncryptedCache(key, cache)
  }
}

async function _getEncryptedCache(key) {
  const cache = localStorage.getItem('x-cache')

  if (cache) {
    const decrypted = await _decryptData(key, cache)

    return JSON.parse(decrypted)
  }

  return {}
}

async function _encryptData(key, data = '') {
  if (!key) {
    throw new Error('No key provided')
  }

  const encoder = new TextEncoder()
  const encodedData = encoder.encode(data)

  key = key.padEnd(16, '0').substring(0, 16)

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(key),
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )

  const iv = window.crypto.getRandomValues(new Uint8Array(12))

  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv
    },
    cryptoKey,
    encodedData
  )

  // Convert encrypted data and iv to Base64 and concatenate them
  const encryptedBase64 = Buffer.from(encrypted).toString('base64')
  const ivBase64 = Buffer.from(iv).toString('base64')
  const result = ivBase64 + ':' + encryptedBase64

  return result
}

async function _decryptData(key, encryptedDataWithIv) {
  if (!key) {
    throw new Error('No key provided')
  }

  const encoder = new TextEncoder()
  const decoder = new TextDecoder()

  key = key.padEnd(16, '0').substring(0, 16)

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(key),
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )

  // Split the encrypted data and iv
  const [ivBase64, encryptedBase64] = encryptedDataWithIv.split(':')
  const iv = Buffer.from(ivBase64, 'base64')
  const encryptedData = Buffer.from(encryptedBase64, 'base64')

  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: iv
    },
    cryptoKey,
    encryptedData
  )

  return decoder.decode(decrypted)
}
